import React, { useEffect, useMemo } from 'react';
import { EMPTY } from 'utils/atomUtils';
import SelectInput from './SelectInput';
import { filterTimeOptions, getErrorMessage, TimeNotice } from './utils';
import StartTimeIcon from './StartTimeIcon';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { useTimeOptions } from './hooks/useTimeOptions';
import { useTimePickerData } from './hooks/useTimePickerData';
import { useAnalytics as trackAvailableTimes } from './hooks/useAnalytics';
import { TimePickerProps } from './types';

// Main component
const TimePicker: React.FC<TimePickerProps> = ({
  onChange = () => {},
  modalVersion = false,
  editable = true,
  calendar,
  error = null,
  useDuration = false,
  isOwner = false,
}) => {
  const data = useTimePickerData();
  const { timepickerTestFlag, trackAvailableTimesFlagEnabled } = useFeatureFlags();

  const options = useTimeOptions({ calendar, useDuration }, data, timepickerTestFlag);

  const timeOptions = useMemo(() => filterTimeOptions(options, timepickerTestFlag), [options, timepickerTestFlag]);

  const errorToShow = getErrorMessage(options, calendar, error);

  const isDisabled = !editable || options.length === 0;

  const notice = timepickerTestFlag ? <TimeNotice /> : null;

  trackAvailableTimes(data, timeOptions, timepickerTestFlag, trackAvailableTimesFlagEnabled);

  // handle the case when tripTime is set and not available in timeOptions
  useEffect(() => {
    if (data.tripTime) {
      // Handle both object and string cases
      const currentValue = typeof data.tripTime === 'object' ? data.tripTime.value : data.tripTime;

      // Check if current tripTime value exists in timeOptions
      const isTimeAvailable = timeOptions.some((option) => option.value === currentValue);

      if (!isTimeAvailable) {
        data.setTripTime(EMPTY);
      }
    }
  }, [timeOptions, data.tripTime, data.setTripTime]);

  return (
    <div data-testid="pdp-booking-widget-time-picker-container">
      <SelectInput
        name="TimePicker"
        object="start_time_picker"
        error={errorToShow}
        modalVersion={modalVersion}
        icon={<StartTimeIcon />}
        options={timeOptions}
        defaultValue={data.tripTime}
        onChange={onChange}
        setter={data.setTripTime}
        title="Start time"
        isDisabled={isDisabled}
        isOwner={isOwner}
        notice={notice}
      />
    </div>
  );
};

export default TimePicker;
